import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { SoundOutlined } from '@ant-design/icons'
import { Switch, Divider } from 'antd'
import { Text } from 'ui/styled'
import { getFromStorage, setToStorage } from 'utils/localStorage'
import { objectMap } from 'utils/helpers'
import {
  GROUPED_REPLY_TYPE,
  NOTIFICATIONS,
  ALARMS
} from 'context/Notifications/constants'
import Notification from './Notification'
import { StyledDrawer, StyledTabs, SwitchRow } from './styled'

const generateReply = (v, k) => {
  const lastItem = v?.length && v[v.length - 1]
  return {
    id: lastItem.id,
    ids: v.map(item => item.id),
    source_id: k,
    source_type: lastItem.source_type,
    created_at: lastItem.created_at,
    data: lastItem.data,
    type: GROUPED_REPLY_TYPE
  }
}

const getGroupedReplies = items => {
  let result = []

  if (items) result = Object.values(objectMap(items, generateReply))

  return result
}

const DrawerComponent = ({
  onClose,
  visible,
  generalNotifications,
  alarms,
  groupedReplies
}) => {
  const [activeMenu, setActiveMenu] = useState(visible)
  const [enableSound, setEnableSound] = useState(
    getFromStorage('isSoundEnabled')()
  )

  const onSwitchChange = checked => {
    setToStorage('isSoundEnabled')(checked)
    setEnableSound(checked)
  }

  // Sort with oldest on top
  const notifications = useMemo(() => {
    const array = [
      ...generalNotifications,
      ...getGroupedReplies(groupedReplies)
    ]
    return array.sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10))
  }, [generalNotifications, groupedReplies])

  const tabItems = useMemo(
    () => [
      {
        label: (
          <span data-test="notificationCenterAlarmsTab">
            Alarms ({alarms.length})
          </span>
        ),
        key: ALARMS,
        children: alarms?.length ? (
          alarms?.map(item => <Notification key={item.id} item={item} />)
        ) : (
          <Text>No alarms</Text>
        )
      },
      {
        label: (
          <span data-test="notificationCenterNotificationsTab">
            Notifications ({notifications.length})
          </span>
        ),
        key: NOTIFICATIONS,
        children: notifications?.length ? (
          notifications?.map(item => <Notification key={item.id} item={item} />)
        ) : (
          <Text>No notifications</Text>
        )
      }
    ],
    [alarms, notifications]
  )

  return (
    <StyledDrawer
      mask={{ enabled: true, blur: false }}
      placement="right"
      onClose={onClose}
      open={!!visible}
      closable={false}
    >
      <Divider style={{ margin: '0 0 10px 0' }} />
      <StyledTabs
        data-test="notificationCenterTabs"
        defaultActiveKey={NOTIFICATIONS}
        activeKey={activeMenu}
        onChange={key => setActiveMenu(key)}
        centered
        tabBarExtraContent={
          <SwitchRow>
            <SoundOutlined style={{ fontSize: '20px' }} />
            <Switch checked={enableSound} onChange={onSwitchChange} />
          </SwitchRow>
        }
        items={tabItems}
      />
    </StyledDrawer>
  )
}

DrawerComponent.propTypes = {
  onClose: PropTypes.func.isRequired,
  visible: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  generalNotifications: PropTypes.array.isRequired,
  alarms: PropTypes.array.isRequired,
  groupedReplies: PropTypes.object.isRequired
}

export default DrawerComponent
